import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary component to catch and handle React errors gracefully
 * Prevents the entire app from crashing due to component errors
 */
class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null,
        errorInfo: null
    };

    public static getDerivedStateFromError(error: Error): Partial<State> {
        return { hasError: true, error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('Uncaught error:', error, errorInfo);
        this.setState({ errorInfo });

        // Log to external service in production if needed
        if (import.meta.env.PROD) {
            // Example: Sentry.captureException(error);
        }
    }

    private handleReload = () => {
        window.location.reload();
    };

    private handleGoHome = () => {
        window.location.href = '/';
    };

    public render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen flex items-center justify-center bg-surface-50 dark:bg-surface-950 px-4">
                    <div className="max-w-md w-full text-center space-y-6">
                        {/* Error Icon */}
                        <div className="flex justify-center">
                            <div className="p-4 bg-kirmizi-50 dark:bg-kirmizi-900/20 rounded-2xl border border-kirmizi-200 dark:border-kirmizi-800">
                                <svg
                                    className="w-12 h-12 text-kirmizi-600 dark:text-kirmizi-400"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                    />
                                </svg>
                            </div>
                        </div>

                        {/* Error Message */}
                        <div className="space-y-2">
                            <h1 className="font-poppins font-bold text-2xl text-gray-900 dark:text-white">
                                Bir Hata Oluştu
                            </h1>
                            <p className="text-gray-600 dark:text-gray-400 font-merriweather text-sm">
                                Üzgünüz, bir şeyler ters gitti. Sayfayı yenileyerek tekrar deneyebilirsiniz.
                            </p>
                        </div>

                        {/* Error Details (Development Only) */}
                        {import.meta.env.DEV && this.state.error && (
                            <div className="mt-4 p-4 bg-surface-100 dark:bg-surface-900 rounded-lg border border-surface-200 dark:border-surface-700 text-left">
                                <p className="text-xs font-mono text-gray-600 dark:text-gray-400 break-all">
                                    {this.state.error.message}
                                </p>
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            <button
                                onClick={this.handleReload}
                                className="btn-primary px-6 py-3 rounded-xl shadow-lg shadow-mavi-500/20"
                            >
                                Sayfayı Yenile
                            </button>
                            <button
                                onClick={this.handleGoHome}
                                className="btn-secondary px-6 py-3 rounded-xl"
                            >
                                Ana Sayfaya Dön
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
